import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, Download, Hotel, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND}/api/document-shares/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="max-w-md rounded-none border p-8 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="mt-4 font-heading text-xl font-bold">Link unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="share-error">{error}</p>
        </Card>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-10" data-testid="share-page">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground"><Hotel className="h-5 w-5" /></div>
          <div>
            <div className="font-heading text-lg font-black tracking-tight">GCG HOTELS</div>
            <div className="label-caps text-[0.6rem] text-muted-foreground">Shared Documents</div>
          </div>
        </div>
        <Card className="rounded-none border p-6">
          <div className="mb-4">
            <h1 className="font-heading text-2xl font-black">{data.property_name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Shared by {data.shared_by} · {data.documents.length} document(s)
              {data.expires_at ? ` · expires ${new Date(data.expires_at).toLocaleDateString()}` : ""}
            </p>
          </div>
          <div className="divide-y divide-border border-t border-border">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-4 py-3" data-testid={`share-doc-${d.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{d.title}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="secondary" className="rounded-none">{d.folder}</Badge>
                      {d.period && <span>{d.period}</span>}
                      {d.size ? <span>{fmtSize(d.size)}</span> : null}
                    </div>
                  </div>
                </div>
                <a href={`${BACKEND}/api/document-shares/${token}/file/${d.id}?download=1`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid={`share-download-${d.id}`}><Download className="mr-2 h-4 w-4" /> Download</Button>
                </a>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
